import { t } from 'ttag';
import * as invert from 'lodash/invert';

import {
  GenderEnum,
  ShowRealNameEnum,
  ShowSocialWebSiteEnum,
} from '@models';
import { OrderStatus, WarehouseType } from '@newModels';
import DressIcon from '@svg/dress.svg';
import ParfumeIcon from '@svg/parfume.svg';
import MenClothIcon from '@svg/men-cloth.svg';
import PramIcon from '@svg/pram.svg';
import HomeIcon from '@svg/home.svg';
import TeddyLiteIcon from '@svg/teddy-lite.svg';
import SportLiteIcon from '@svg/sport-lite.svg';
import AccessoriesLiteIcon from '@svg/accessories.svg';
import ElectronicsIcon from '@svg/electronics.svg';
import AutoIcon from '@svg/auto.svg';
import PetIcon from '@svg/pet.svg';
import GardenIcon from '@svg/garden.svg';
import BuildingIcon from '@svg/building.svg';

export const MAX_SAFE_PURCHASE_COST = 1000;
export const CSRF_TOKEN_HEADER_NAME = 'X-CSRFToken';

export enum MyClothesSortTypes {
  BY_POPULAR = '1',
  PRICE_ASC = '2',
  PRICE_DESC = '3',
  DATE_DESC = '4',
}

export enum EventKey {
  Enter = 'Enter',
  Backspace = 'Backspace',
  ArrowDown = 'ArrowDown',
  ArrowUp = 'ArrowUp',
  Escape = 'Escape',
}

export enum BuyerActionWithPurchase {
  LeaveReview,
  Write,
  OrderDetails,
  DeclineOrder,
  Delete,
  Pay,
  Return,
}

export enum SellerActionWithSale {
  LeaveReview,
  Write,
  ApproveOrder,
  MarkSold,
  OrderDetails,
  DeclineOrder,
  Delete,
}

export enum MessageSendingStatus {
  // Mutation sent, awaiting response from server
  Pending,

  // Message successfully sent to the server
  Sent,

  // Recipient read message
  Read,
}

export const FILTER_QUERIES = {
  CATALOG: 'catalog',
  SORT: 'sort',
  SEARCH_TEXT: 'search_text',
} as const;

export const orderStatusSlugMapping: Record<OrderStatus, string> = {
  [OrderStatus.Created]: 'new',
  [OrderStatus.Confirmed]: 'confirmed',
  [OrderStatus.Done]: 'done',
  [OrderStatus.Declined]: 'declined',
  [OrderStatus.Archived]: 'archived',
};
export const slugOrderStatusMapping = invert(
  orderStatusSlugMapping,
) as Record<string, OrderStatus>;

export const orderStatusVerbose = () => ({
  [OrderStatus.Created]: t`Нове`,
  [OrderStatus.Confirmed]: t`Прийняте`,
  [OrderStatus.Done]: t`Виконане`,
  [OrderStatus.Declined]: t`Відхилене`,
  [OrderStatus.Archived]: t`Архівне`,
});

export const showRealNameVerbose = () => ({
  [ShowRealNameEnum.ShowRealNameForEveryone]: t`Всім`,
  [ShowRealNameEnum.ShowRealNameForRegisteredUsers]: t`Тільки для зареєстрованих користувачів`,
  [ShowRealNameEnum.ShowRealNameForNobody]: t`Нікому`,
});

export const showSocialWebSiteVerbose = () => ({
  [ShowSocialWebSiteEnum.Yes]: t`Так`,
  [ShowSocialWebSiteEnum.No]: t`Ні`,
});

export const genderVerbose = () => ({
  [GenderEnum.GenderMale]: t`Чоловік`,
  [GenderEnum.GenderFemale]: t`Жінка`,
});

export const getWarehouseTypeOptions = () => ([
  {
    label: t`Поштомат`,
    value: WarehouseType.Postomate,
  },
  {
    label: t`Відділення`,
    value: WarehouseType.PostOffice,
  },
]);

export const PRODUCT_PAGE_REFERRER = {
  CATALOG: 'catalog',
  CATALOG_SEARCH: 'search',
  DYNAMIC_COLLECTION: 'dynamic_collection',
  SOCIAL_PRODUCT_POST: 'social_feed',
} as const;

export const BRAND_PAGE_REFERRER = {
  BRANDS: 'brand_page',
  PRODUCT: 'brand_product_details',
  SEO_HUB: 'brand_hub',
} as const;

// 1m 30s in milliseconds
export const debtLevelCacheLifetime = 90000;
// 2 hours
export const navProfileCacheLifetime = 2 * 60 * 60 * 1000;

export const reCaptchaSitekey = '6LdcnWMUAAAAAP7GhumlYqHzYKcVcvabQgO_Kbyp';
export const reCaptchaApiSrc = (
  'https://www.google.com/recaptcha/api.js?explicit=true&onload=onRecaptchaApiLoaded'
);
export const shafaFacebookPageUrl = 'https://www.facebook.com/shafa.ua';
export const shafaInstagramPageUrl = 'https://www.instagram.com/shafaua';
export const shafaYoutubePageUrl = 'https://www.youtube.com/channel/UCWn_Jh5KFT_EImWHt7oCGDg';
export const shafaAndroidAppUrl = 'https://play.google.com/store/apps/details?id=shafa.odejda.obuv.aksessuary';
export const shafaIOSAppUrl = 'https://apple.co/2pE66cJ';
export const shafaSupportEmail = 'support@shafa.com.ua';
export const SUPPORT_FORM_URL = 'https://shafa.kayako.com/ru/conversation/new';

export const emailRegexp = new RegExp(
  [
    '^[a-zA-Z0-9.!#$%&\'*+/=?^_`{|}~-]+',
    '@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?',
    '(?:\\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$',
  ].join(''),
);

export const shopNameRegexp = /^[a-zA-Zа-яА-Я-0-9іёїґє_ -]+$/i;
export const phoneNumberRegexp = /^\+?[0-9]*$/;
export const cyrillicWordRegexp = /^[а-яА-ЯяёЁЇїІіЄєҐґ\s\-ʼ']*$/;
export const ukrLetterRegexp = /^[а-щА-ЩЬьЮюЯяЇїІіЄєҐґ\s\-ʼ']*$/i;
// TODO (a.kravchuk): add localized regexp
export const shafaUrlRegexp = /^(https?:\/\/)?shafa\.ua\/?/;
export const shafaPromoUrlRegexp = /^(https?:\/\/)?promo\.shafa\.ua\/?/;
export const kayakoUrlRegexp = /^(https?:\/\/)?shafa\.kayako\.com\/?/;
export const rozetkaDeliveryRegexp = /^(https:\/\/)?rozetka\.delivery\/?/;
export const checkboxRegexp = /^(https:\/\/)?check\.checkbox\.ua\/?/;

export const lengthPhoneNumber = 13;

export const maxLengthShopName = 15;
export const minLengthShopName = 5;
export const maxSavedAnswers = 5;

export const UNKNOWN_ERROR_MESSAGE = () => t`Упс щось пішло не так, спробуйте пізніше`;
export const NOVA_POSHTA_UNKNOWN_ERROR_MESSAGE = t`Нова Пошта тимчасово недоступна. Спробуйте пізніше`;
export const AUTH_ERROR_MESSAGE = 'must authenticate';
export const AUTH_ERROR_MESSAGE_LEGACY = 'User not authenticated.';
export const USER_BANNED_FRAUD_ERROR_MESSAGE = 'USER_BANNED_FRAUD';
export const USER_BANNED_FRAUD_ERROR_MESSAGE_VERBOSE = t`Вас заблокували за шахрайство`;
export const MARK_SOLD_SUCCESS_MESSAGE_VERBOSE = () => t`Вітаємо з успішним продажем!`;

// COOKIE KEYS
export const DOWNLOAD_APP_COOKIE = 'mobile_download_app_cookie';
export const APPS_BANNER_COOKIE = 'mobile_banner_cookie';
export const COLLECTING_EMAILS_COOKIE = 'collecting_emails_cookie';
export const SAVED_SEARCH_POPUP = 'saved-search-popup_cookie';
export const UTM_LABELS_COOKIE = 'saved_utm_labels';
export const SOCIAL_LOGIN_COOKIE = 'social_login';
export const ADULT_USER_COOKIE = 'adult_user';
export const CANARY_COOKIE = 'canary';
export const SWAP_PARTY_COOKIE = 'swap-party';

// Key for cookie with information about social user from backend
export const socialAuthInfoCookieKey = 'social_auth_info_cookie';
export const isAdminCookieKey = 'is_admin';

// LOCAL/SESSION STORAGE KEYS
export const debtLevelCacheKey = 'debtLevel';
export const recentlyViewedProductsKey = 'recentlyViewedProducts';
export const navProfileKey = 'navProfile';
export const subsProductsBrandPopupKey = 'subsProductsBrandPopup';
export const notAuthUsersEmailKey = 'notAuthUsersEmail';
export const appsPromotionBannerKey = 'appsPromotionBanner';
export const mainPageShowedBannersKey = 'mainPageShowedBanners';
export const mainPageIsBannerShownKey = 'mainPageIsBannerShown';
export const closedLasReviewRequest = 'closedLasReviewRequest';
export const lastProductFeatureBannerKey = 'lastProductFeatureBanner';
export const productCollectionSlugKey = 'productCollectionSlug';
export const ignoredUnpaidOrdersKey = 'ignoredUnpaidOrdersKey';
export const previousFirstPlaceFeatureBannerPriorityKey = 'previousFirstPlaceFeatureBannerPriority';
export const rozetkaTTNPrintNotifiedKey = 'rozetkaTTNPrintNotifiedKey';
export const superSellerAdvantagesOpenKey = 'superSellerAdvantagesOpen';
export const sellerAchievementsRecommendationsOpenKey = 'sellerAchievementsRecommendationsOpen';
export const updateNotificationsKey = 'updateNotifications';
export const isSellerKey = 'isSeller';
export const addPaymentCardModalKey = 'addPaymentCardModal';
export const messageToSubscribersOverviewOpenKey = 'messageToSubscribersOverviewOpen';
export const messageToSubscribersModalShownKey = 'messageToSubscribersModalShown';
export const indexActionBannerKey = 'indexActionBannerKey';
export const availabilityFeedbackProvidedCountKey = 'availabilityFeedbackProvidedCountKey';
export const checkoutSmartPromoModalShownKey = 'checkoutSmartPromoModalShown';
export const productReferrerKey = 'productReferrer';
export const brandReferrerKey = 'brandReferrer';
export const utmLabelsKey = 'utmLabels';
export const openedTopProductIdKey = 'openedTopProductId';
export const analiticAppliedFiltersKey = 'analiticAppliedFilters';
export const productReferrerPathnameKey = 'productReferrerPathname';
export const productFormGuidelinesStorageKey = 'productFormGuidelinesStorage';

// sessionStorage key consist of `productId` string + actual productId
export const productIdKey = 'productId';

export const cardConfirmedFrameMessage = 'CARD_CONFIRMED';

export const NOT_SELECTED_CITY_ID = 2;

export const maxDiscountSale = 90;

export const novelties = 'novinki';

export const MADE_IN_UKRAINE_CHARACTERISTIC_GROUP_ID = 1790;

export const CHANGE_RECEIVING_PAYMENT_METHOD_ERROR_CODES = {
  CARD_DOES_NOT_EXIST: 'card_does_not_exist',
  AT_LEAST_ONE_PRODUCT_REQUIRED: 'at_least_one_product_required',
  ACQUIRING_INFO_DOES_NOT_EXIST: 'acquiring_info_does_not_exist',
  ACQUIRING_IN_PROGRESS: 'acquiring_in_progress',
} as const;

export const UPDATE_PAYMENT_METHOD_ERROR_CODES = {
  PAYMENT_CARD_REQUIRED: 'payment_card_required',
} as const;

export const COSMETIC_CATALOG_START_PATH = '00010002000F';
export const SHOES_CATALOG_START_PATH = '00010004000A';
export const OUTLET_DYNAMIC_COLLECTION_SLUG = 'outlet';

export const ADSENSE_BLACKLIST_URLS = [
  // # Put there some url where u wanna block ads, the example u can see below
  // # '/women/nizhnee-bele-i-kupalniki',
];

// add here new label if added new root category SHAF-153
export const NAMESPACES = [
  {
    id: 1,
    slug: 'women',
    name: t`Жіночі речі`,
    icon: DressIcon,
  },
  {
    id: 2,
    slug: 'kids',
    name: t`Дитячі речі`,
    icon: PramIcon,
  },
  {
    id: 3,
    slug: 'men',
    name: t`Чоловічі речі`,
    icon: MenClothIcon,
  },
  {
    id: 4,
    slug: 'home',
    name: t`Для дому`,
    icon: HomeIcon,
  },
  {
    id: 5,
    slug: 'cosmetics',
    name: t`Краса та здоров'я`,
    icon: ParfumeIcon,
  },
  {
    id: 6,
    slug: 'forkids',
    name: t`Дитячі товари`,
    icon: TeddyLiteIcon,
  },
  {
    id: 7,
    slug: 'sport',
    name: t`Хобі та спорт`,
    icon: SportLiteIcon,
  },
  {
    id: 8,
    slug: 'aksesuary',
    name: t`Аксесуари`,
    icon: AccessoriesLiteIcon,
  },
  {
    id: 9,
    slug: 'electronics',
    name: t`Електроніка`,
    icon: ElectronicsIcon,
  },
  {
    id: 10,
    slug: 'auto',
    name: t`Автотовари`,
    icon: AutoIcon,
  },
  {
    id: 11,
    slug: 'pet',
    name: t`Зоотовари`,
    icon: PetIcon,
  },
  {
    id: 12,
    slug: 'garden',
    name: t`Дача і сад`,
    icon: GardenIcon,
  },
  {
    id: 13,
    slug: 'building',
    name: t`Будівництво`,
    icon: BuildingIcon,
  },
];
